import { useAuth0, withAuthenticationRequired } from "ct-auth"
import { useEffect } from "react"
import { Navigate } from "react-router-dom"
import { isTrainer, isManager, isSponsor, setLastKnownOrgId, shouldBounceUser, userAuth0OrgId, isEmbedded } from "../../data-utils/authUtils.js"
import { ROUTE_PATHS } from "../../utilities/constants"
import Loader from "../common/Loader/loader"

export const AuthenticationGuard = ({ component, allowUserRole = false, allowSponsorRole = true, allowTrainerRole = false, allowManagerRole = true, noAccessRedirectPath = ROUTE_PATHS.GAME_ROOT }) => {
  const { user, logout, isAuthenticated, isLoading, loginWithPopup } = useAuth0()
  const embedded = isEmbedded()

  // Persist user's org ID for subsequent logins (SSO/MFA org context)
  useEffect(() => {
    if (user) {
      const auth0OrgId = userAuth0OrgId(user)
      setLastKnownOrgId(auth0OrgId)
    }
  }, [user])

  if (shouldBounceUser(user)) {
    // Immediately log user out if JWT marked with 'bounce' claim by Auth0 provider
    logout({
      logoutParams: {
        returnTo: window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_CALLBACK_URL,
      },
    })
  }

  // Role gate → a <Navigate/> when the current role isn't allowed, else null.
  const roleRedirect = () => {
    if (!user) return null
    if (isManager(user)) return allowManagerRole ? null : <Navigate to={noAccessRedirectPath} />
    if (isSponsor(user)) return allowSponsorRole ? null : <Navigate to={noAccessRedirectPath} />
    if (isTrainer(user)) return allowTrainerRole ? null : <Navigate to={noAccessRedirectPath} />
    if (allowUserRole === false) return <Navigate to={noAccessRedirectPath} />
    return null
  }

  // App-shell embedded mode: Auth0's login page can't render in an iframe, so
  // authenticate via a popup (user gesture) instead of an in-frame redirect.
  if (embedded) {
    if (isLoading) {
      return (
        <div className="App container-fluid page-loading" style={{ background: "#000000" }}>
          <Loader />
        </div>
      )
    }
    if (!isAuthenticated) {
      return (
        <div className="App container-fluid page-loading" style={{ background: "#000000" }}>
          <div className="d-flex flex-column align-items-center gap-3 text-white">
            <p className="m-0">Sign in to Prepare to view it here.</p>
            <button type="button" className="btn btn-primary" onClick={() => loginWithPopup()}>
              Sign in to Prepare
            </button>
          </div>
        </div>
      )
    }
    const redirect = roleRedirect()
    if (redirect) return redirect
    const Component = component
    return <Component />
  }

  // Standalone / production — unchanged Auth0 redirect flow.
  const Component = withAuthenticationRequired(component, {
    returnTo: () => {
      const returnURL = `${window.location.pathname}${window.location.search}${window.location.hash}`
      return returnURL
    },
    onRedirecting: () => (
      <div className="App container-fluid page-loading" style={{ background: "#000000" }}>
        <Loader />
      </div>
    ),
  })

  const redirect = roleRedirect()
  if (redirect) return redirect
  return <Component />
}
